import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
  afterNextRender,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap, take } from 'rxjs/operators';

import { PaymentService } from '../../payment/payment.service';
import { StripeTransaction } from '../../product/models/models';

interface PaymentMethodForm {
  cardholderName: FormControl<string>;
}

@Component({
  selector: 'app-account-payment-method',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './account-payment-method.component.html',
  styleUrl: './account-payment-method.component.scss',
})
export class AccountPaymentMethodComponent implements OnDestroy {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly paymentService = inject(PaymentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly document = inject(DOCUMENT);

  readonly isSubmitting = signal(false);
  readonly submitError = signal('');
  readonly isCardValid = signal(false);

  private stripe: any;
  private cardNumber: any;
  private cardExpiry: any;
  private cardCvc: any;
  private cardHandler = this.onCardChange.bind(this);

  form: FormGroup<PaymentMethodForm> = this.fb.group({
    cardholderName: ['', [Validators.required]],
  });

  @Output() canceled = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  @ViewChild('cardNumberElement') cardNumberElement: ElementRef;
  @ViewChild('cardExpiryElement') cardExpiryElement: ElementRef;
  @ViewChild('cardCvcElement') cardCvcElement: ElementRef;

  constructor() {
    afterNextRender(() => {
      this.paymentService.ensureApiKeyLoaded();
      this.paymentService
        .loadStripeJs()
        .pipe(
          switchMap(() =>
            this.paymentService.stripePublicApiKey$.pipe(
              filter(apiKey => !!apiKey),
              take(1),
            )
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(apiKey => {
          const stripeConstructor = (this.document.defaultView as any)?.Stripe;
          if (stripeConstructor) {
            this.stripe = stripeConstructor(apiKey);
            this.initializeStripeElements();
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroyStripeElements();
  }

  private onCardChange(event: any): void {
    const isCardNumberValid = event?.complete && !event?.error;
    if (isCardNumberValid !== this.isCardValid()) {
      this.isCardValid.set(isCardNumberValid);
    }
    if (event) {
      this.cd.detectChanges();
    }
  }

  private initializeStripeElements(): void {
    const stripeElements = this.stripe.elements();

    const elementStyles = {
      base: {
        color: '#495057',
        fontWeight: 400,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '14px',
        lineHeight: '25px',
        fontSmoothing: 'antialiased',
        ':focus': {
          color: '#2e2a28',
        },
        '::placeholder': {
          color: '#9CA3AF',
        },
        ':focus::placeholder': {
          color: '#9CA3AF',
        },
      },
      invalid: {
        color: '#f00',
        ':focus': {
          color: '#000',
        },
        '::placeholder': {
          color: '#FFCCA5',
        },
      },
    };

    const elementClasses = {
      focus: 'focus:ring-2 focus:ring-[#0084C4]',
    };

    // Wrapper to track all three elements
    const updateCardValidity = () => {
      const allValid =
        this.cardNumber?.complete &&
        this.cardExpiry?.complete &&
        this.cardCvc?.complete &&
        !this.cardNumber?.error &&
        !this.cardExpiry?.error &&
        !this.cardCvc?.error;
      this.isCardValid.set(!!allValid);
    };

    this.cardNumber = stripeElements.create('cardNumber', {
      placeholder: '',
      style: elementStyles,
      classes: elementClasses,
    });
    this.cardNumber.mount(this.cardNumberElement.nativeElement);
    this.cardNumber.addEventListener('change', (event: any) => {
      updateCardValidity();
      this.onCardChange(event);
    });

    this.cardExpiry = stripeElements.create('cardExpiry', {
      placeholder: 'MM/YY',
      style: elementStyles,
      classes: elementClasses,
    });
    this.cardExpiry.mount(this.cardExpiryElement.nativeElement);
    this.cardExpiry.addEventListener('change', (event: any) => {
      updateCardValidity();
      this.onCardChange(event);
    });

    this.cardCvc = stripeElements.create('cardCvc', {
      placeholder: '123',
      style: elementStyles,
      classes: elementClasses,
    });
    this.cardCvc.mount(this.cardCvcElement.nativeElement);
    this.cardCvc.addEventListener('change', (event: any) => {
      updateCardValidity();
      this.onCardChange(event);
    });
  }

  private destroyStripeElements(): void {
    if (this.cardNumber) {
      this.cardNumber.removeEventListener('change', this.cardHandler);
      this.cardNumber.destroy();
    }
    if (this.cardExpiry) {
      this.cardExpiry.removeEventListener('change', this.cardHandler);
      this.cardExpiry.destroy();
    }
    if (this.cardCvc) {
      this.cardCvc.removeEventListener('change', this.cardHandler);
      this.cardCvc.destroy();
    }
  }

  private async createStripeToken(data: any): Promise<any> {
    return await this.stripe.createToken(this.cardNumber, { data });
  }

  onCancel(): void {
    this.canceled.emit();
  }

  onSubmit(): void {
    this.submitError.set('');

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.stripe || !this.cardNumber) {
      this.submitError.set('Payment form is unavailable. Please refresh and try again.');
      return;
    }

    this.isSubmitting.set(true);

    this.createStripeToken({ name: this.form.controls.cardholderName.value })
      .then((result: { token?: any; error?: { message?: string } }) => {
        if (result?.error || !result?.token) {
          this.submitError.set(
            result?.error?.message || 'Unable to validate your card. Please try again.'
          );
          this.isSubmitting.set(false);
          return;
        }

        const transaction = new StripeTransaction();
        transaction.Token = result.token;

        this.paymentService
          .createCustomerWithCard(transaction)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.isSubmitting.set(false);
              this.saved.emit();
            },
            error: (err: any) => {
              const serverMessage = err?.error?.Message || err?.error?.message;
              this.submitError.set(
                serverMessage ||
                  'Your card was declined. Please check your card details or try a different payment method.'
              );
              this.isSubmitting.set(false);
            },
          });
      })
      .catch(() => {
        this.submitError.set('Unable to validate your card. Please try again.');
        this.isSubmitting.set(false);
      });
  }
}
